/**
 * Key-value store for per-device user preferences (language, theme). Preferences are
 * conveniences: a failing store must never break the application.
 */
export interface PreferenceStore {
  /**
   * Reads a value.
   *
   * @param key - The key.
   * @returns The value or `null`.
   */
  read(key: string): string | null;

  /**
   * Writes a value; failures are ignored.
   *
   * @param key - The key.
   * @param value - The value.
   */
  write(key: string, value: string): void;
}

/**
 * Creates a store on top of Web Storage that tolerates blocked or unavailable storage.
 *
 * @param getStorage - Returns the storage; may throw (private mode, blocked site data).
 * @returns The store.
 */
export function createWebPreferenceStore(getStorage: () => Storage): PreferenceStore {
  const store: PreferenceStore = {
    read(key) {
      try {
        const value = getStorage().getItem(key);
        return value;
      } catch {
        return null;
      }
    },
    write(key, value) {
      try {
        getStorage().setItem(key, value);
      } catch {
        // Preferences are optional; the in-memory value stays active for this session.
      }
    },
  };

  return store;
}

/**
 * Creates an in-memory store (tests and environments without Web Storage).
 *
 * @returns The store.
 */
export function createMemoryPreferenceStore(): PreferenceStore {
  const values = new Map<string, string>();
  const store: PreferenceStore = {
    read(key) {
      const value = values.get(key) ?? null;
      return value;
    },
    write(key, value) {
      values.set(key, value);
    },
  };

  return store;
}
